import { useContext, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Button, Dialog, DialogContent, DialogTitle, MenuItem, SelectField, Switch,
} from '../../../../../2-component';
import { Box, Grid, Typography } from '../../../../../1-primative';
import { ThemeSelect } from '../../../../../3-pattern/themeSelect/themeSelect';
import { translateBackend } from '../../../../../../assets/i18n/config';
import {
  SubAccount, SubAccountStates, SubAccountTransitions, TRADING_PROCESS_LIST,
} from '../../../../../../interfaces';
import { FETCH_MODEL_PORTFOLIOS, UPDATE_SUB_ACCOUNT, FETCH_SUB_ACCOUNT } from '../../../changeTheme/changeTheme.queries';
import { usePermissions, UserContext } from '../../../../../../providers/userContextProvider';
import { FieldRequiredType } from '../../../../configurableOptionFields';
import { invalidFields } from '../../../../workflowCompletion/subSteps/utils';
import { TRANSITION_SUB_ACCOUNT } from '../../../../../../pages/subAccountsDetail';
import { GET_SUB_ACCOUNT_STATE } from '../editSubAccount';

const END_LIQUIDATION = gql`
  mutation endLiquidation($input: EndLiquidationInput!) {
    endLiquidation(input: $input) {
      subAccount { id }
    }
  }
`;

export default function EditPortfolioTheme({
  open, setOpen, options, objectId,
}: { open: boolean, setOpen: (open: boolean) => void, options: any, objectId: string }) {
  const { t } = useTranslation(['components', 'subAccountsDetail', 'workflowCompletions']);
  const { activeOrganization } = useContext(UserContext);
  const { permissions } = usePermissions();

  const [liquidated, setLiquidated] = useState(false);
  const [portfolios, setPortfolios] = useState<any[]>();
  const [object, setObject] = useState<any>(null);
  const [focused, setFocused] = useState<string[]>([]);
  const [invalidFieldsList, setInvalidFieldsList] = useState<string[]>([]);
  const [subAccountUpdateData, setSubAccountUpdateData] = useState<Partial<SubAccount>>();
  const [subAccountState, setSubAccountState] = useState();

  const showAllThemes = permissions.includes('read:all_themes');

  const { refetch } = useQuery(FETCH_SUB_ACCOUNT, {
    variables: { subAccountId: objectId },
    onCompleted: (subAccountData) => {
      const d = subAccountData.fetchSubAccount.subAccount;
      setObject(d);
      setLiquidated(d?.liquidated);
      setSubAccountState(d?.state);
      setSubAccountUpdateData({
        subAccountState: d?.state,
        modelPortfolio: d?.financialProduct,
        portfolioTheme: d?.theme || d?.financialProduct?.theme,
        tradingProcess: d?.tradingProcess,
        skipIPS: d?.skipIPS,
        allowClientDeposits: d?.allowClientDeposits,
        isPartial: d?.isPartial,
        liquidated: d?.liquidated,
      });
    },
    fetchPolicy: 'no-cache',
  });

  const { loading: modelPortfoliosLoading } = useQuery(FETCH_MODEL_PORTFOLIOS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
          themeId: subAccountUpdateData?.portfolioTheme?.id,
          state: 'ACTIVE',
          suggestable: showAllThemes ? undefined : true,
        },
        pagination: { perPage: 1000 },
      },
    },
    onCompleted: (d: any) => {
      setPortfolios(d.fetchModelPortfolios.modelPortfolios);
    },
    fetchPolicy: 'no-cache',
  });

  const [updateTheme, { loading: updateThemeLoading }] = useMutation(UPDATE_SUB_ACCOUNT, {
    variables: {
      input: {
        subAccountId: objectId,
        themeId: subAccountUpdateData?.portfolioTheme?.id,
        financialProductId: subAccountUpdateData?.modelPortfolio?.id,
        skipIPS: options.requireInvestorPolicyStatement ? subAccountUpdateData?.skipIPS : undefined,
        isPartial: options.allowFractionalShares ? subAccountUpdateData?.isPartial : undefined,
        allowClientDeposits: options.allowClientDeposits ? subAccountUpdateData?.allowClientDeposits : undefined,
        tradingProcess: subAccountUpdateData?.tradingProcess,
      },
    },
  });

  const [endLiquidation, { loading: endLiquidationLoading }] = useMutation(END_LIQUIDATION, {
    variables: {
      input: {
        subAccountId: objectId,
      },
    },
  });

  const getTransition = () => {
    if (subAccountUpdateData?.subAccountState === SubAccountStates.AWAITING_APPROVAL) {
      return SubAccountTransitions.REVIEW;
    }
    return SubAccountTransitions.ACTIVATE;
  };

  const [transitionSubAccount, { loading: transitionSubAccountLoading }] = useMutation(TRANSITION_SUB_ACCOUNT, {
    variables: {
      input: {
        subAccountId: objectId,
        transition: getTransition(),
      },
    },
    onCompleted: refetch,
    refetchQueries: [GET_SUB_ACCOUNT_STATE()],
    awaitRefetchQueries: true,
  });

  const subAccountWithGoalPortfolio = Boolean(object?.goal?.financialProduct);

  const error = (key: string): boolean => (
    ((!subAccountUpdateData?.[key] && focused.includes(key)) || invalidFieldsList.includes(key)) && options?.[key]?.required !== FieldRequiredType.NOT_REQUIRED
  );

  const validate = () => {
    const fields = invalidFields({ ...options }, subAccountUpdateData as any);
    setFocused(fields);
    setInvalidFieldsList(fields);
    return fields.length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    if (!liquidated && subAccountUpdateData?.liquidated) {
      await endLiquidation();
    }
    if (subAccountUpdateData?.subAccountState !== subAccountState) {
      await transitionSubAccount();
    }
    await updateTheme();
    setOpen(false);
  };

  const onClose = () => {
    setOpen(false);
    setSubAccountUpdateData({
      subAccountState: object?.state,
      modelPortfolio: object?.financialProduct,
      portfolioTheme: object?.theme || object?.financialProduct?.theme,
      tradingProcess: object?.tradingProcess,
      skipIPS: object?.skipIPS,
      allowClientDeposits: object?.allowClientDeposits,
      isPartial: object?.isPartial,
      liquidated: object?.liquidated,
    });
    setSubAccountState(object?.state);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle onClose={onClose}>{t('subAccountsDetail:editSubAccount')}</DialogTitle>
      <DialogContent>
        {options?.subAccountState?.enabled && permissions.includes('transition:account') && (
          <SelectField
            label={translateBackend(options?.subAccountState?.label || {})}
            disabled={options?.subAccountState?.required === FieldRequiredType.NOT_EDITABLE}
            value={subAccountUpdateData?.subAccountState}
            onChange={(e: any) => setSubAccountUpdateData({ ...subAccountUpdateData, subAccountState: e.target.value })}
            sx={{ mt: 2 }}
            fullWidth
            error={error('subAccountState')}
          >
            {Object.values(SubAccountStates).filter((x: any) => x !== SubAccountStates.INACTIVE).map((x: any) => (
              <MenuItem key={x} value={x} disabled={x === SubAccountStates.AWAITING_APPROVAL && subAccountUpdateData?.subAccountState === SubAccountStates.ACTIVE}>
                {t(`subAccountsDetail:state.${x}`)}
              </MenuItem>
            ))}
          </SelectField>
        )}
        {options?.portfolioTheme?.enabled && (
          <ThemeSelect
            label={translateBackend(options?.portfolioTheme?.label || {})}
            disabled={subAccountWithGoalPortfolio || options?.portfolioTheme?.required === FieldRequiredType.NOT_EDITABLE}
            onThemeSelect={(th: any) => setSubAccountUpdateData({ ...subAccountUpdateData, portfolioTheme: th })}
            selectedTheme={subAccountUpdateData?.portfolioTheme}
            error={error('portfolioTheme')}
            sx={{ mt: 2 }}
            suitabilityScore={object?.suitabilityScore}
          />
        )}
        {options?.portfolioTheme?.enabled && (
          <Typography variant='bodyLarge' sx={{ mt: 2 }}>
            {translateBackend(subAccountUpdateData?.portfolioTheme?.translatedDescription || {})}
          </Typography>
        )}
        {options?.modelPortfolio?.enabled && (
          <SelectField
            label={translateBackend(options?.modelPortfolio?.label || {})}
            disabled={!subAccountUpdateData?.portfolioTheme || modelPortfoliosLoading || subAccountWithGoalPortfolio || options?.modelPortfolio?.required === FieldRequiredType.NOT_EDITABLE}
            value={subAccountUpdateData?.modelPortfolio?.id}
            onChange={(e: any) => setSubAccountUpdateData({ ...subAccountUpdateData, modelPortfolio: portfolios?.find((x: any) => x.id === e.target.value) })}
            sx={{ mt: 2 }}
            fullWidth
            error={error('modelPortfolio')}
          >
            {portfolios && portfolios?.length > 0 && portfolios?.map((p: any) => (
              <MenuItem key={p.id} value={p.id}>
                {translateBackend(p.translatedName)}{object?.suggestedFinancialProduct?.id === p.id && ` (${t('components:changeTheme.suggested')})`}
              </MenuItem>
            ))}
          </SelectField>
        )}
        {options?.tradingProcess?.enabled && (
          <SelectField
            label={translateBackend(options?.tradingProcess?.label || {})}
            disabled={options?.tradingProcess?.required === FieldRequiredType.NOT_EDITABLE}
            value={subAccountUpdateData?.tradingProcess}
            onChange={(e: any) => setSubAccountUpdateData({ ...subAccountUpdateData, tradingProcess: e.target.value })}
            sx={{ mt: 2 }}
            error={error('tradingProcess')}
            fullWidth
          >
            {TRADING_PROCESS_LIST.map((x: any) => (
              <MenuItem key={x.value} value={x.value}>{x.name}</MenuItem>
            ))}
          </SelectField>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {options.requireInvestorPolicyStatement && (
            <Grid item xs={12}>
              <Switch
                label={t('workflowCompletions:subAccounts.requireIPS')}
                checked={!subAccountUpdateData?.skipIPS}
                onChange={(onoff: boolean) => setSubAccountUpdateData({ ...subAccountUpdateData, skipIPS: !onoff })}
                labelPosition='left'
                fullWidth
              />
            </Grid>
          )}
          {options.allowFractionalShares && (
            <Grid item xs={12}>
              <Switch
                label={t('workflowCompletions:subAccounts.allowFractionalShares')}
                checked={subAccountUpdateData?.isPartial}
                onChange={(onoff: boolean) => setSubAccountUpdateData({ ...subAccountUpdateData, isPartial: onoff })}
                labelPosition='left'
                fullWidth
              />
            </Grid>
          )}
          {options.allowClientDeposits && (
            <Grid item xs={12}>
              <Switch
                label={t('workflowCompletions:subAccounts.allowClientDeposits')}
                checked={subAccountUpdateData?.allowClientDeposits}
                onChange={(onoff: boolean) => setSubAccountUpdateData({ ...subAccountUpdateData, allowClientDeposits: onoff })}
                labelPosition='left'
                fullWidth
              />
            </Grid>
          )}
          {options.subAccountLiquidated && permissions.includes('write:subAccount_liquidation') && (
            <Grid item xs={12}>
              <Switch
                label={t('workflowCompletions:subAccounts.subAccountLiquidated')}
                checked={liquidated}
                onChange={(onoff: boolean) => setLiquidated(onoff)}
                labelPosition='left'
                disabled={!subAccountUpdateData?.liquidated}
                fullWidth
              />
            </Grid>
          )}
        </Grid>
        <Box display='flex' justifyContent='flex-end' sx={{ mt: 3 }}>
          <Button label={t('components:changeTheme.save')} onClick={submit} disabled={updateThemeLoading || endLiquidationLoading || transitionSubAccountLoading || modelPortfoliosLoading} />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
